import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import hitIcon from '../../../../../assets/icons/hit.png';
import missIcon from '../../../../../assets/icons/miss.png';
import backIcon from '../../../../../assets/icons/back-arrow.png';
import { scoringService } from '../../../../../services';
import './DashboardScore.css';
import './ScoreShoot.css';

const DEFAULT_TARGET_LABELS = ['A', 'B', 'C', 'D', 'E', 'F'];
const DEFAULT_STAND_COUNT = 1;
const DEFAULT_STAND_DATA = { standName: '', shots: [] };
const TARGET_TYPE_OPTIONS = [
    { value: 'single', label: 'Single' },
    { value: 'double', label: 'Double' },
    { value: 'a&e', label: 'A&E' },
    { value: 'sim_pair', label: 'Sim Pair' },
    { value: 'report_pair', label: 'Report Pair' }
];
const DIRECTION_OPTIONS = [
    { value: '', label: 'Direction (optional)' },
    { value: 'l-r', label: 'Left to Right' },
    { value: 'r-l', label: 'Right to Left' },
    { value: 'incoming', label: 'Incoming' },
    { value: 'outgoing', label: 'Outgoing' },
    { value: 'teal', label: 'Teal' },
    { value: 'looping', label: 'Looping' },
    { value: 'rabbit', label: 'Rabbit' },
    { value: 'overhead', label: 'Overhead' }
];
const DEFAULT_TARGET_METADATA = { targetType: 'single', direction: '', distance: '' };

const cloneTargetMetadata = (metadata = DEFAULT_TARGET_METADATA) => ({
    targetType: metadata.targetType || DEFAULT_TARGET_METADATA.targetType,
    direction: metadata.direction || DEFAULT_TARGET_METADATA.direction,
    distance: metadata.distance ?? DEFAULT_TARGET_METADATA.distance
});

const getShooterKey = (eventShooterId, shooterName) => {
    if (eventShooterId) {
        return `event-${eventShooterId}`;
    }

    return `name-${shooterName || 'fun-shoot'}`;
};

const parseStandData = (round) => {
    if (!round?.scoreData) {
        return DEFAULT_STAND_DATA;
    }

    try {
        const scoreData = JSON.parse(round.scoreData);
        const shots = Array.isArray(scoreData.shots)
            ? scoreData.shots.map((shot, index) => ({
                id: `${round.roundId || round.roundNumber}-${index}`,
                label: shot.targetLabel || `Target ${index + 1}`,
                result: typeof shot.hit === 'boolean' ? shot.hit : null,
                recordedAt: shot.recordedAt || null,
                targetType: shot.targetType || '',
                distance: shot.distance ?? '',
                direction: shot.direction || ''
            }))
            : [];

        return {
            standName: scoreData.standName || '',
            shots
        };
    } catch (error) {
        console.error('Error parsing fun shoot round data:', error);
        return DEFAULT_STAND_DATA;
    }
};

const buildShooterStandMap = (rounds = [], shooters = []) => {
    const defaultShooterKey = shooters.length > 0
        ? getShooterKey(shooters[0].eventShooterId, shooters[0].shooterName)
        : getShooterKey(null, 'Fun Shoot');

    return rounds.reduce((map, round) => {
        let parsedScoreData = {};
        try {
            parsedScoreData = round?.scoreData ? JSON.parse(round.scoreData) : {};
        } catch {
            parsedScoreData = {};
        }

        const matchedShooter = shooters.find((shooter) =>
            (parsedScoreData.eventShooterId && shooter.eventShooterId === parsedScoreData.eventShooterId) ||
            (round.shooterName && shooter.shooterName && shooter.shooterName === round.shooterName)
        );

        const shooterKey = matchedShooter
            ? getShooterKey(matchedShooter.eventShooterId, matchedShooter.shooterName)
            : defaultShooterKey;
        const standNumber = round.roundNumber || 1;

        if (!map[shooterKey]) {
            map[shooterKey] = {};
        }

        map[shooterKey][standNumber] = parseStandData(round);
        return map;
    }, {});
};

const getStandCount = (scoreSheet, standShots) => {
    const standNumbers = Object.keys(standShots).map(Number).filter(Boolean);
    const highestRecordedStand = standNumbers.length > 0 ? Math.max(...standNumbers) : 0;

    if (scoreSheet?.clayConfigJson) {
        try {
            const config = JSON.parse(scoreSheet.clayConfigJson);

            if (Array.isArray(config?.stands)) {
                return Math.max(config.stands.length, highestRecordedStand, DEFAULT_STAND_COUNT);
            }

            if (Array.isArray(config)) {
                return Math.max(config.length, highestRecordedStand, DEFAULT_STAND_COUNT);
            }

            if (typeof config?.totalStands === 'number') {
                return Math.max(config.totalStands, highestRecordedStand, DEFAULT_STAND_COUNT);
            }
        } catch (error) {
            console.error('Error parsing clay config json:', error);
        }
    }

    return Math.max(highestRecordedStand, DEFAULT_STAND_COUNT);
};

const normaliseStandConfig = (scoreSheet) => {
    if (!scoreSheet?.clayConfigJson) {
        return [];
    }

    try {
        const parsed = JSON.parse(scoreSheet.clayConfigJson);
        const standList = Array.isArray(parsed?.stands)
            ? parsed.stands
            : Array.isArray(parsed)
                ? parsed
                : [];

        return standList.map((stand, index) => {
            const standNumber = Number(stand?.standNumber || stand?.number || stand?.stand || index + 1);
            const targetLabels = Array.isArray(stand?.targetLabels)
                ? stand.targetLabels
                : Array.isArray(stand?.targets)
                    ? stand.targets.map((target, targetIndex) =>
                        typeof target === 'string'
                            ? target
                            : target?.label || target?.name || target?.targetLabel || `T${targetIndex + 1}`
                    )
                    : Array.isArray(stand?.clays)
                        ? stand.clays.map((target, targetIndex) =>
                            typeof target === 'string'
                                ? target
                                : target?.label || target?.name || target?.targetLabel || `T${targetIndex + 1}`
                        )
                        : [];
            const presentationsByLabel = Array.isArray(stand?.presentations)
                ? stand.presentations.reduce((accumulator, presentation) => {
                    const label = String(presentation?.label || '').trim();
                    if (!label || accumulator[label]) {
                        return accumulator;
                    }

                    accumulator[label] = {
                        targetType: presentation?.targetType || DEFAULT_TARGET_METADATA.targetType,
                        direction: presentation?.direction || DEFAULT_TARGET_METADATA.direction,
                        distance: presentation?.distance != null ? String(presentation.distance) : DEFAULT_TARGET_METADATA.distance
                    };

                    return accumulator;
                }, {})
                : {};

            return {
                standNumber,
                standName: stand?.standName || stand?.name || stand?.label || '',
                targetLabels: targetLabels.filter(Boolean),
                presentationsByLabel
            };
        });
    } catch (error) {
        console.error('Error parsing stand config:', error);
        return [];
    }
};

const getTemplateMetadataForLabel = (standTemplate, label) => {
    if (!standTemplate?.presentationsByLabel || !label) {
        return cloneTargetMetadata();
    }

    return cloneTargetMetadata(standTemplate.presentationsByLabel[label] || DEFAULT_TARGET_METADATA);
};

const getHighestDefaultTargetCount = (shots = []) => {
    const highestIndex = shots.reduce((highest, shot) => {
        const label = typeof shot?.label === 'string' ? shot.label.trim().toUpperCase() : '';
        const labelIndex = DEFAULT_TARGET_LABELS.indexOf(label);
        return labelIndex >= 0 ? Math.max(highest, labelIndex + 1) : highest;
    }, 0);

    return highestIndex > 0 ? highestIndex : 2;
};

const findNextStandNumberForShooter = (standMap = {}, totalStandCount = DEFAULT_STAND_COUNT) => {
    return Array.from({ length: totalStandCount }, (_, index) => index + 1)
        .find((standNumber) => (standMap[standNumber]?.shots || []).some((shot) => shot.result === null))
        || Array.from({ length: totalStandCount }, (_, index) => index + 1)
            .find((standNumber) => !(standMap[standNumber]?.shots || []).length)
        || null;
};

const FunShootScoring = () => {
    const { scoreSheetId } = useParams();
    const navigate = useNavigate();

    const [scoreSheet, setScoreSheet] = useState(null);
    const [currentStand, setCurrentStand] = useState(1);
    const [standShotsByShooter, setStandShotsByShooter] = useState({});
    const [currentShooterKey, setCurrentShooterKey] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSavingShot, setIsSavingShot] = useState(false);
    const [error, setError] = useState(null);
    const [customTargetLabel, setCustomTargetLabel] = useState('');
    const [manualStandCounts, setManualStandCounts] = useState({});
    const [expandedStandCount, setExpandedStandCount] = useState(DEFAULT_STAND_COUNT);
    const [autoRotateShooters, setAutoRotateShooters] = useState(false);
    const [shooterOrder, setShooterOrder] = useState([]);
    const [draftTargetMetadata, setDraftTargetMetadata] = useState(DEFAULT_TARGET_METADATA);
    const [showDraftMetadataEditor, setShowDraftMetadataEditor] = useState(false);
    const [expandedShotEditors, setExpandedShotEditors] = useState({});

    const fetchScoreSheet = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const data = await scoringService.getScoreSheet(scoreSheetId);
            const savedStandShots = buildShooterStandMap(data.rounds || [], data.shooters || []);

            setScoreSheet(data);
            setStandShotsByShooter(savedStandShots);
            setExpandedStandCount(getStandCount(data, savedStandShots));
        } catch (fetchError) {
            console.error('Error fetching score sheet:', fetchError);
            setError('Failed to load score sheet');
        } finally {
            setIsLoading(false);
        }
    }, [scoreSheetId]);

    useEffect(() => {
        if (scoreSheetId) {
            fetchScoreSheet();
        }
    }, [scoreSheetId, fetchScoreSheet]);

    const shooters = useMemo(() => {
        const scoreSheetShooters = Array.isArray(scoreSheet?.shooters) ? scoreSheet.shooters : [];
        const mappedShooters = scoreSheetShooters.map((shooter, index) => ({
            key: getShooterKey(shooter.eventShooterId, shooter.shooterName),
            eventShooterId: shooter.eventShooterId,
            shooterName: shooter.shooterName || `Shooter ${index + 1}`
        }));

        if (mappedShooters.length > 0) {
            return mappedShooters;
        }

        return [{
            key: getShooterKey(null, 'Fun Shoot'),
            eventShooterId: null,
            shooterName: 'Fun Shoot'
        }];
    }, [scoreSheet]);

    useEffect(() => {
        setShooterOrder((previousOrder) => {
            const shooterKeySet = new Set(shooters.map((shooter) => shooter.key));
            const retainedOrder = previousOrder.filter((key) => shooterKeySet.has(key));
            const missingKeys = shooters
                .map((shooter) => shooter.key)
                .filter((key) => !retainedOrder.includes(key));

            return [...retainedOrder, ...missingKeys];
        });
    }, [shooters]);

    const orderedShooters = useMemo(() => {
        if (!shooterOrder.length) {
            return shooters;
        }

        const shooterByKey = new Map(shooters.map((shooter) => [shooter.key, shooter]));
        const ordered = shooterOrder
            .map((key) => shooterByKey.get(key))
            .filter(Boolean);

        if (ordered.length === shooters.length) {
            return ordered;
        }

        const existingKeys = new Set(ordered.map((shooter) => shooter.key));
        const missing = shooters.filter((shooter) => !existingKeys.has(shooter.key));
        return [...ordered, ...missing];
    }, [shooters, shooterOrder]);

    const standConfig = useMemo(() => normaliseStandConfig(scoreSheet), [scoreSheet]);
    const currentStandTemplate = useMemo(
        () => standConfig.find((stand) => stand.standNumber === currentStand) || null,
        [standConfig, currentStand]
    );
    const currentStandPresentationDefaults = currentStandTemplate?.presentationsByLabel || {};
    const hasSavedPresentationDefaults = Object.keys(currentStandPresentationDefaults).length > 0;

    useEffect(() => {
        if (!currentStandTemplate?.targetLabels?.length || !hasSavedPresentationDefaults) {
            return;
        }

        const firstLabel = currentStandTemplate.targetLabels[0];
        setDraftTargetMetadata(getTemplateMetadataForLabel(currentStandTemplate, firstLabel));
        setShowDraftMetadataEditor(false);
    }, [currentStandTemplate, hasSavedPresentationDefaults]);

    useEffect(() => {
        if (!currentShooterKey && orderedShooters.length > 0) {
            setCurrentShooterKey(orderedShooters[0].key);
        }
    }, [currentShooterKey, orderedShooters]);

    useEffect(() => {
        if (!orderedShooters.length) {
            return;
        }

        const hasSelectedShooter = orderedShooters.some((shooter) => shooter.key === currentShooterKey);
        if (!hasSelectedShooter) {
            setCurrentShooterKey(orderedShooters[0].key);
        }
    }, [orderedShooters, currentShooterKey]);

    const totalStandCount = useMemo(
        () => Math.max(getStandCount(scoreSheet, standShotsByShooter[currentShooterKey] || {}), expandedStandCount),
        [scoreSheet, standShotsByShooter, currentShooterKey, expandedStandCount]
    );

    const currentShooter = orderedShooters.find((shooter) => shooter.key === currentShooterKey) || orderedShooters[0];
    const canManage = scoreSheet?.canManage !== false;
    const currentStandData = useMemo(
        () => standShotsByShooter[currentShooterKey]?.[currentStand] || DEFAULT_STAND_DATA,
        [standShotsByShooter, currentShooterKey, currentStand]
    );
    const currentShots = useMemo(() => currentStandData.shots || [], [currentStandData]);

    const { totalHits, totalShots, shooterHits, shooterShots } = useMemo(() => {
        const allShots = Object.values(standShotsByShooter)
            .flatMap((standMap) => Object.values(standMap))
            .flatMap((stand) => stand.shots || []);
        const selectedShooterShots = Object.values(standShotsByShooter[currentShooterKey] || {})
            .flatMap((stand) => stand.shots || []);

        return {
            totalHits: allShots.filter((shot) => shot.result === true).length,
            totalShots: allShots.filter((shot) => shot.result !== null).length,
            shooterHits: selectedShooterShots.filter((shot) => shot.result === true).length,
            shooterShots: selectedShooterShots.filter((shot) => shot.result !== null).length
        };
    }, [standShotsByShooter, currentShooterKey]);

    const percentage = totalShots > 0 ? ((totalHits / totalShots) * 100).toFixed(1) : '0.0';
    const shooterPercentage = shooterShots > 0 ? ((shooterHits / shooterShots) * 100).toFixed(1) : '0.0';
    const shooterSummaries = useMemo(() => {
        return orderedShooters.map((shooter) => {
            const standMap = standShotsByShooter[shooter.key] || {};
            const shots = Object.values(standMap).flatMap((stand) => stand.shots || []);
            const completedShots = shots.filter((shot) => shot.result !== null).length;
            const hits = shots.filter((shot) => shot.result === true).length;
            const nextStandNumber = findNextStandNumberForShooter(standMap, totalStandCount);

            return {
                ...shooter,
                completedShots,
                hits,
                nextStandNumber
            };
        });
    }, [orderedShooters, standShotsByShooter, totalStandCount]);

    const nextIncompleteShooter = shooterSummaries.find((shooter) => shooter.key !== currentShooterKey && shooter.nextStandNumber);
    const shooterSummaryByKey = useMemo(
        () => Object.fromEntries(shooterSummaries.map((summary) => [summary.key, summary])),
        [shooterSummaries]
    );
    const currentTargetCount = useMemo(() => {
        if (currentStandTemplate?.targetLabels?.length) {
            return currentStandTemplate.targetLabels.length;
        }

        if (manualStandCounts[currentStand]) {
            return manualStandCounts[currentStand];
        }

        return getHighestDefaultTargetCount(currentShots);
    }, [currentShots, currentStand, currentStandTemplate, manualStandCounts]);

    const targetLabels = useMemo(() => {
        if (currentStandTemplate?.targetLabels?.length) {
            return currentStandTemplate.targetLabels;
        }

        return DEFAULT_TARGET_LABELS.slice(0, currentTargetCount);
    }, [currentStandTemplate, currentTargetCount]);

    const updateStandData = useCallback((standNumber, updater) => {
        setStandShotsByShooter((previous) => {
            const shooterStands = previous[currentShooterKey] || {};
            const existingStandData = shooterStands[standNumber] || DEFAULT_STAND_DATA;
            return {
                ...previous,
                [currentShooterKey]: {
                    ...shooterStands,
                    [standNumber]: updater(existingStandData)
                }
            };
        });
    }, [currentShooterKey]);

    useEffect(() => {
        if (!currentStandTemplate?.standName || currentStandData.standName) {
            return;
        }

        updateStandData(currentStand, (standData) => ({
            ...standData,
            standName: standData.standName || currentStandTemplate.standName
        }));
    }, [currentStand, currentStandData.standName, currentStandTemplate, updateStandData]);

    useEffect(() => {
        setExpandedShotEditors({});
    }, [currentStand, currentShooterKey]);

    const addTarget = (label) => {
        if (!canManage) {
            return;
        }

        const templateMetadata = getTemplateMetadataForLabel(currentStandTemplate, label);
        const nextMetadata = hasSavedPresentationDefaults
            ? templateMetadata
            : cloneTargetMetadata(draftTargetMetadata);

        updateStandData(currentStand, (standData) => ({
            ...standData,
            shots: [
                ...(standData.shots || []),
                {
                    id: `pending-${currentShooterKey}-${currentStand}-${label}-${(standData.shots || []).length + 1}`,
                    label,
                    result: null,
                    recordedAt: null,
                    targetType: nextMetadata.targetType || 'single',
                    distance: nextMetadata.distance || '',
                    direction: nextMetadata.direction || ''
                }
            ]
        }));
    };

    const addCustomTarget = () => {
        if (!canManage) {
            return;
        }

        const label = customTargetLabel.trim();
        if (!label) {
            return;
        }

        addTarget(label);
        setCustomTargetLabel('');
    };

    const removeLastPendingTarget = () => {
        if (!canManage) {
            return;
        }

        updateStandData(currentStand, (standData) => {
            const shots = standData.shots || [];
            const lastPendingIndex = [...shots].reverse().findIndex((shot) => shot.result === null);
            if (lastPendingIndex === -1) {
                return standData;
            }

            const actualIndex = shots.length - 1 - lastPendingIndex;
            return {
                ...standData,
                shots: shots.filter((_, index) => index !== actualIndex)
            };
        });
    };

    const updateStandName = (value) => {
        if (!canManage) {
            return;
        }

        updateStandData(currentStand, (standData) => ({
            ...standData,
            standName: value
        }));
    };

    const updateDraftTargetMetadata = (field, value) => {
        if (!canManage) {
            return;
        }

        setDraftTargetMetadata((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    const updateShotMetadata = (index, field, value) => {
        if (!canManage) {
            return;
        }

        updateStandData(currentStand, (standData) => ({
            ...standData,
            shots: (standData.shots || []).map((existingShot, shotIndex) =>
                shotIndex === index
                    ? {
                        ...existingShot,
                        [field]: value
                    }
                    : existingShot
            )
        }));
    };

    const recordShot = async (index, hit) => {
        const shot = currentShots[index];
        if (!canManage || !shot || shot.result !== null || isSavingShot) {
            return;
        }

        setIsSavingShot(true);
        setError(null);

        try {
            const parsedDistance = Number(shot.distance);
            const distance = Number.isFinite(parsedDistance) && parsedDistance > 0
                ? parsedDistance
                : null;

            await scoringService.recordFlexibleShot(scoreSheetId, {
                standNumber: currentStand,
                eventShooterId: currentShooter?.eventShooterId,
                standName: currentStandData.standName || '',
                targetLabel: shot.label,
                hit,
                targetType: shot.targetType || '',
                direction: shot.direction || '',
                distance
            });

            updateStandData(currentStand, (standData) => ({
                ...standData,
                shots: (standData.shots || []).map((existingShot, shotIndex) =>
                    shotIndex === index
                        ? {
                            ...existingShot,
                            result: hit,
                            recordedAt: new Date().toISOString()
                        }
                        : existingShot
                )
            }));
        } catch (recordError) {
            console.error('Error recording shot:', recordError);
            setError('Failed to record shot');
        } finally {
            setIsSavingShot(false);
        }
    };

    const goToStand = (standNumber) => {
        setCurrentStand(standNumber);
    };

    const nextStand = () => {
        setCurrentStand((previous) => Math.min(previous + 1, totalStandCount));
    };

    const previousStand = () => {
        setCurrentStand((previous) => Math.max(previous - 1, 1));
    };

    const setStandTargetCount = (count) => {
        if (!canManage) {
            return;
        }

        setManualStandCounts((previous) => ({
            ...previous,
            [currentStand]: count
        }));
    };

    const moveToNextShooter = useCallback(() => {
        if (!canManage) {
            return;
        }

        if (orderedShooters.length <= 1) {
            return;
        }

        const currentIndex = Math.max(
            orderedShooters.findIndex((shooter) => shooter.key === currentShooterKey),
            0
        );
        const nextShooter = orderedShooters[(currentIndex + 1) % orderedShooters.length];
        const nextSummary = shooterSummaryByKey[nextShooter.key];

        setCurrentShooterKey(nextShooter.key);
        if (nextSummary?.nextStandNumber) {
            setCurrentStand(nextSummary.nextStandNumber);
        }
    }, [canManage, orderedShooters, currentShooterKey, shooterSummaryByKey]);

    const shuffleShooterOrder = useCallback(() => {
        if (!canManage) {
            return;
        }

        if (orderedShooters.length <= 1) {
            return;
        }

        const shuffled = [...orderedShooters];
        for (let i = shuffled.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        setShooterOrder(shuffled.map((shooter) => shooter.key));
    }, [canManage, orderedShooters]);

    const wrapCurrentStand = () => {
        if (!canManage) {
            return;
        }

        if (hasPendingShots) {
            return;
        }

        if (autoRotateShooters && orderedShooters.length > 1) {
            moveToNextShooter();
            return;
        }

        if (currentStand >= totalStandCount) {
            const nextStandNumber = totalStandCount + 1;
            setExpandedStandCount(nextStandNumber);
            setCurrentStand(nextStandNumber);
            return;
        }

        nextStand();
    };

    const finishScoring = async () => {
        if (!canManage) {
            return;
        }

        setError(null);

        try {
            await scoringService.finishScoreSheet(scoreSheetId);

            navigate(`/dashboard/score/summary/${scoreSheetId}`);
        } catch (finishError) {
            console.error('Error finalising fun shoot:', finishError);
            setError('Failed to finalise this fun shoot.');
        }
    };

    const hasPendingShots = currentShots.some((shot) => shot.result === null);
    const pendingTargetCount = currentShots.filter((shot) => shot.result === null).length;
    const currentStandComplete = currentShots.length > 0 && !hasPendingShots;

    if (isLoading) {
        return <div className="loading-container">Loading...</div>;
    }

    if (error && !scoreSheet) {
        return <div className="error-container">{error}</div>;
    }

    return (
        <div className="score-shoot-container">
            <div className="score-header">
                <button onClick={() => navigate('/dashboard/score')} className="back-button">
                    <img src={backIcon} alt="Back" />
                </button>
                <h2>{scoreSheet?.eventName || 'Fun Shoot'} Scoring</h2>
            </div>

            {error && <div className="error-container">{error}</div>}

            <div className="score-summary-card">
                <div className="score-summary-content">
                    <div className="score-summary-item">
                        <span className="score-label">Shooter</span>
                        <span className="score-value">{currentShooter?.shooterName || 'Fun Shoot'}</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Stand</span>
                        <span className="score-value">{currentStand}</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Shooter Score</span>
                        <span className="score-value">{shooterHits}/{shooterShots}</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Shooter %</span>
                        <span className="score-value">{shooterPercentage}%</span>
                    </div>
                </div>
            </div>

            {orderedShooters.length > 1 && (
                <div className="fun-shoot-shooter-tabs">
                    {shooterSummaries.map((shooter) => (
                        <button
                            key={shooter.key}
                            type="button"
                            className={`fun-shoot-shooter-tab ${shooter.key === currentShooterKey ? 'active' : ''}`}
                            onClick={() => setCurrentShooterKey(shooter.key)}
                        >
                            <span>{shooter.shooterName}</span>
                            <small>
                                {shooter.nextStandNumber ? `Next stand ${shooter.nextStandNumber}` : 'No open stands'}
                                {' · '}
                                {shooter.hits}/{shooter.completedShots}
                            </small>
                        </button>
                    ))}
                </div>
            )}

            <div className="instructions-card">
                {canManage ? (
                    <>
                        <p>Tap the clay label, then record hit or miss. Saved stand defaults are applied automatically where available.</p>
                        <p>Move between shooters and stands whenever the squad changes position.</p>
                    </>
                ) : (
                    <>
                        <p>You can view this score sheet because you were added as a shooter.</p>
                        <p>This view is read only. Only the event owner or Shoot Connect staff can record fun-shoot results.</p>
                    </>
                )}
            </div>

            <div className="fun-shoot-toolbar">
                <div className="fun-shoot-toolbar-chip">
                    {currentStandComplete ? 'Stand complete' : `${pendingTargetCount} target${pendingTargetCount === 1 ? '' : 's'} pending`}
                </div>
                <div className="fun-shoot-toolbar-actions">
                    {canManage && orderedShooters.length > 1 && (
                        <>
                            <button
                                type="button"
                                className="fun-shoot-toolbar-button secondary"
                                onClick={() => setAutoRotateShooters((previous) => !previous)}
                            >
                                {autoRotateShooters ? 'Auto Rotate: On' : 'Auto Rotate: Off'}
                            </button>
                            <button
                                type="button"
                                className="fun-shoot-toolbar-button secondary"
                                onClick={moveToNextShooter}
                            >
                                Next Shooter
                            </button>
                            <button
                                type="button"
                                className="fun-shoot-toolbar-button secondary"
                                onClick={shuffleShooterOrder}
                            >
                                Shuffle Shooters
                            </button>
                        </>
                    )}
                    {canManage && !hasPendingShots && currentShots.length > 0 && (
                        <button
                            type="button"
                            className="fun-shoot-toolbar-button secondary"
                            onClick={wrapCurrentStand}
                        >
                            Wrap This Stand
                        </button>
                    )}
                    {nextIncompleteShooter && (
                        <button
                            type="button"
                            className="fun-shoot-toolbar-button"
                            onClick={() => {
                                setCurrentShooterKey(nextIncompleteShooter.key);
                                if (nextIncompleteShooter.nextStandNumber) {
                                    setCurrentStand(nextIncompleteShooter.nextStandNumber);
                                }
                            }}
                        >
                            Jump to {nextIncompleteShooter.shooterName}
                        </button>
                    )}
                </div>
            </div>

            <div className="target-selection">
                <div className="fun-shoot-stand-header">
                    <div>
                        <h3>Stand {currentStand}</h3>
                        <p>
                            {currentStandTemplate?.standName
                                ? `Template: ${currentStandTemplate.standName}`
                                : 'Capture what this shooter actually saw on this stand.'}
                        </p>
                    </div>
                    <div className="fun-shoot-overall-score">
                        <span>Overall</span>
                        <strong>{totalHits}/{totalShots}</strong>
                        <small>{percentage}%</small>
                    </div>
                </div>
                {canManage ? (
                    <>
                        {currentStandTemplate?.targetLabels?.length > 0 && (
                            <div className="fun-shoot-template-card">
                                <strong>Saved stand template</strong>
                                <div className="fun-shoot-template-tags">
                                    {currentStandTemplate.targetLabels.map((label) => (
                                        <span key={label} className="fun-shoot-template-tag">{label}</span>
                                    ))}
                                </div>
                                {hasSavedPresentationDefaults && (
                                    <p className="fun-shoot-template-copy">
                                        Type, direction, and distance defaults are preloaded from the saved stand setup.
                                    </p>
                                )}
                            </div>
                        )}
                        <div className="fun-shoot-stand-name">
                            <label htmlFor="fun-shoot-stand-name">Stand name or note</label>
                            <input
                                id="fun-shoot-stand-name"
                                type="text"
                                value={currentStandData.standName || ''}
                                onChange={(e) => updateStandName(e.target.value)}
                                placeholder={currentStandTemplate?.standName || 'Tower pair, rabbit bank, crossing left-right...'}
                            />
                        </div>
                        {!currentStandTemplate?.targetLabels?.length && (
                            <div className="fun-shoot-target-count">
                                <span className="fun-shoot-target-count-label">Available clay labels on this stand</span>
                                <div className="fun-shoot-target-count-buttons">
                                    {DEFAULT_TARGET_LABELS.map((label, index) => {
                                        const count = index + 1;
                                        return (
                                            <button
                                                key={label}
                                                type="button"
                                                className={`fun-shoot-target-count-button ${currentTargetCount === count ? 'active' : ''}`}
                                                onClick={() => setStandTargetCount(count)}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="fun-shoot-target-count-help">
                                    Set the highest clay letter available here, then log each presentation in whatever order the shooter actually takes them.
                                </p>
                            </div>
                        )}
                        <div className="fun-shoot-target-meta-card">
                            <div className="fun-shoot-target-meta-header">
                                <div>
                                    <strong>Default presentation for new targets</strong>
                                    <p>
                                        {hasSavedPresentationDefaults
                                            ? 'Saved stand defaults are ready. Open this only if the stand is throwing differently today.'
                                            : 'Set the default once for this stand, then keep adding targets quickly.'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="fun-shoot-toolbar-button secondary"
                                    onClick={() => setShowDraftMetadataEditor((previous) => !previous)}
                                >
                                    {showDraftMetadataEditor ? 'Hide defaults' : 'Adjust defaults'}
                                </button>
                            </div>
                            {(showDraftMetadataEditor || !hasSavedPresentationDefaults) && (
                                <div className="fun-shoot-target-meta-grid">
                                    <label>
                                        Type
                                        <select
                                            value={draftTargetMetadata.targetType}
                                            onChange={(event) => updateDraftTargetMetadata('targetType', event.target.value)}
                                        >
                                            {TARGET_TYPE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label>
                                        Direction
                                        <select
                                            value={draftTargetMetadata.direction}
                                            onChange={(event) => updateDraftTargetMetadata('direction', event.target.value)}
                                        >
                                            {DIRECTION_OPTIONS.map((option) => (
                                                <option key={option.value || 'blank'} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label>
                                        Distance (yards)
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={draftTargetMetadata.distance}
                                            onChange={(event) => updateDraftTargetMetadata('distance', event.target.value)}
                                            placeholder="Optional"
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                        <h3>Add Targets For Stand {currentStand}</h3>
                        <div className="target-buttons">
                            {targetLabels.map((label) => (
                                <button
                                    key={label}
                                    onClick={() => addTarget(label)}
                                    className="target-add-button"
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className="fun-shoot-custom-target">
                            <input
                                type="text"
                                value={customTargetLabel}
                                onChange={(e) => setCustomTargetLabel(e.target.value)}
                                placeholder="Custom target label"
                            />
                            <button type="button" className="target-add-button" onClick={addCustomTarget}>
                                Add
                            </button>
                        </div>
                        <button
                            onClick={removeLastPendingTarget}
                            className="undo-button"
                            disabled={pendingTargetCount === 0}
                        >
                            Remove Last Pending Target
                        </button>
                    </>
                ) : (
                    <>
                        {!!(currentStandData.standName || currentStandTemplate?.standName) && (
                            <div className="fun-shoot-template-card">
                                <strong>{currentStandData.standName || currentStandTemplate?.standName}</strong>
                                <p className="fun-shoot-template-copy">
                                    {currentStandComplete ? 'This stand has been completed.' : `${pendingTargetCount} target${pendingTargetCount === 1 ? '' : 's'} still pending.`}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="fun-shoot-stand-tabs">
                {Array.from({ length: totalStandCount }, (_, index) => {
                    const standNumber = index + 1;
                    const standData = standShotsByShooter[currentShooterKey]?.[standNumber] || DEFAULT_STAND_DATA;
                    const shotsForStand = standData.shots || [];
                    const hitsForStand = shotsForStand.filter((shot) => shot.result === true).length;
                    const completedShots = shotsForStand.filter((shot) => shot.result !== null).length;

                    return (
                        <button
                            key={standNumber}
                            type="button"
                            className={`fun-shoot-stand-tab ${standNumber === currentStand ? 'active' : ''}`}
                            onClick={() => goToStand(standNumber)}
                        >
                            <span>Stand {standNumber}</span>
                            <small>{hitsForStand}/{completedShots}</small>
                        </button>
                    );
                })}
            </div>

            <div className="shots-container">
                {currentShots.length === 0 ? (
                    <p className="no-shots-message">Add target letters above to start this stand.</p>
                ) : (
                    <div className="shots-grid">
                        {currentShots.map((shot, index) => (
                            <div key={shot.id} className="shot-item">
                                <div className="shot-label">{shot.label}</div>
                                <div className="fun-shoot-shot-summary">
                                    <span>{TARGET_TYPE_OPTIONS.find((option) => option.value === (shot.targetType || 'single'))?.label || 'Single'}</span>
                                    <span>{DIRECTION_OPTIONS.find((option) => option.value === (shot.direction || ''))?.label || 'Direction open'}</span>
                                    <span>{shot.distance ? `${shot.distance} yds` : 'Distance open'}</span>
                                    {shot.result === null && (
                                        <button
                                            type="button"
                                            className="fun-shoot-shot-edit-toggle"
                                            onClick={() => setExpandedShotEditors((previous) => ({
                                                ...previous,
                                                [shot.id]: !previous[shot.id]
                                            }))}
                                            disabled={isSavingShot || !canManage}
                                        >
                                            {expandedShotEditors[shot.id] ? 'Hide details' : 'Adjust'}
                                        </button>
                                    )}
                                </div>
                                {canManage && shot.result === null && expandedShotEditors[shot.id] && (
                                    <div className="shot-meta">
                                        <label>
                                            Type
                                            <select
                                                value={shot.targetType || 'single'}
                                                onChange={(event) => updateShotMetadata(index, 'targetType', event.target.value)}
                                                disabled={isSavingShot}
                                            >
                                                {TARGET_TYPE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label>
                                            Direction
                                            <select
                                                value={shot.direction || ''}
                                                onChange={(event) => updateShotMetadata(index, 'direction', event.target.value)}
                                                disabled={isSavingShot}
                                            >
                                                {DIRECTION_OPTIONS.map((option) => (
                                                    <option key={option.value || 'blank'} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label>
                                            Distance
                                            <input
                                                type="number"
                                                min="1"
                                                step="1"
                                                value={shot.distance || ''}
                                                onChange={(event) => updateShotMetadata(index, 'distance', event.target.value)}
                                                disabled={isSavingShot}
                                                placeholder="yds"
                                            />
                                        </label>
                                    </div>
                                )}
                                {shot.result === null ? (
                                    <div className="shot-buttons">
                                        <button
                                            onClick={() => recordShot(index, true)}
                                            className="hit-button"
                                            disabled={isSavingShot || !canManage}
                                        >
                                            <img src={hitIcon} alt="Hit" />
                                            Hit
                                        </button>
                                        <button
                                            onClick={() => recordShot(index, false)}
                                            className="miss-button"
                                            disabled={isSavingShot || !canManage}
                                        >
                                            <img src={missIcon} alt="Miss" />
                                            Miss
                                        </button>
                                    </div>
                                ) : (
                                    <div className={`shot-result ${shot.result ? 'hit' : 'miss'}`}>
                                        {shot.result ? 'Hit recorded' : 'Miss recorded'}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="stand-navigation">
                <button
                    onClick={previousStand}
                    disabled={currentStand === 1}
                    className="nav-button prev-button"
                >
                    Previous Stand
                </button>

                <button
                    onClick={nextStand}
                    className="nav-button next-button"
                    disabled={hasPendingShots || currentStand === totalStandCount}
                >
                    Next Stand
                </button>
            </div>

            {canManage && (
                <div className="finish-container">
                    <button onClick={finishScoring} className="finish-button" disabled={hasPendingShots}>
                        Finish And View Summary
                    </button>
                </div>
            )}
        </div>
    );
};

export default FunShootScoring;
